import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/Home/Home';
import MainLayout from './layouts/MainLayout';

// Route-level code splitting — everything except Home (the most common
// landing page) loads on demand, keeping the initial JS bundle smaller.
const PolicyPage = lazy(() => import('./pages/Policy/PolicyPage'));
const ContactUs = lazy(() => import('./pages/Contact/ContactUs'));
const TrackOrder = lazy(() => import('./pages/Order/TrackOrder'));
const AboutUs = lazy(() => import('./pages/About/AboutUs'));
const SearchPage = lazy(() => import('./pages/Search/SearchPage'));
const Auth = lazy(() => import('./pages/Auth/Auth'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const TicketRise = lazy(() => import('./pages/Support/TicketRise'));
const SupportMessageDetail = lazy(() => import('./pages/Support/SupportMessageDetail'));
const PortalOrders = lazy(() => import('./pages/Support/PortalOrders'));
const PortalTrack = lazy(() => import('./pages/Support/PortalTrack'));
const ProductDetail = lazy(() => import('./pages/Products/ProductDetail'));
const RequestQuote = lazy(() => import('./pages/Quote/RequestQuote'));
const CartPage = lazy(() => import('./pages/Cart/CartPage'));
const CheckoutPage = lazy(() => import('./pages/Checkout/CheckoutPage'));
const OrderSuccess = lazy(() => import('./pages/Checkout/OrderSuccess'));
const CategoriesPage = lazy(() => import('./pages/Categories/CategoriesPage'));
const FavoritesPage = lazy(() => import('./pages/Favorites/FavoritesPage'));
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'));
const OrderHistory = lazy(() => import('./pages/Profile/OrderHistory'));
const BulkInquiryPage = lazy(() => import('./pages/BulkInquiry/BulkInquiryPage'));
const BlogPage = lazy(() => import('./pages/Blog/BlogPage'));
const BlogDetail = lazy(() => import('./pages/Blog/BlogDetail'));
const PrivateLabelingPage = lazy(() => import('./pages/PrivateLabeling/PrivateLabelingPage'));
const WholesalePage = lazy(() => import('./pages/Wholesale/WholesalePage'));
const FAQPage = lazy(() => import('./pages/FAQ/FAQPage'));
const CompanyProfilePage = lazy(() => import('./pages/CompanyProfile/CompanyProfilePage'));
const NotificationDetail = lazy(() => import('./pages/Notifications/NotificationDetail'));
const RefundPage = lazy(() => import('./pages/Refund/RefundPage'));
const CancelledOrders = lazy(() => import('./pages/Profile/CancelledOrders'));
const SitemapPage = lazy(() => import('./pages/Sitemap/SitemapPage'));
import { NetworkStatus } from './components/shared/NetworkStatus';
import { CartProvider } from './context/CartContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { AuthProvider } from './context/AuthContext';
import CartDrawer from './components/cart/CartDrawer';
import LoginModal from './components/auth/LoginModal';
import FloatingActions from './components/layout/FloatingActions';
import ScrollToTop from './components/shared/ScrollToTop';
import PasswordRecoveryRedirect from './components/shared/PasswordRecoveryRedirect';
import { registerPushNotifications } from './utils/pushNotifications';

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin" />
  </div>
);

function App() {
  React.useEffect(() => {
    const t = setTimeout(() => registerPushNotifications(), 5000);
    return () => clearTimeout(t);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <PasswordRecoveryRedirect />
      <AuthProvider>
        <CartProvider>
          <FavoritesProvider>
            <NetworkStatus />
            <CartDrawer />
            <LoginModal />
            <FloatingActions />
            <div className="App overflow-x-clip pt-[0px]">
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* ... routes ... */}
                <Route path="/" element={<Home />} />
                <Route path="/sitemap" element={<SitemapPage />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/login" element={<Auth />} />
                <Route path="/signup" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/order-success" element={<OrderSuccess />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/aboutus" element={<AboutUs />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/category" element={<CategoriesPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/products" element={<SearchPage />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/policies" element={<PolicyPage />} />
                <Route path="/policies/:id" element={<PolicyPage />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/track-order" element={<TrackOrder />} />
                <Route path="/track-order/:id" element={<TrackOrder />} />
                <Route path="/orders" element={<OrderHistory />} />
                <Route path="/orders/:id" element={<OrderHistory />} />
                <Route path="/portal/track" element={<TrackOrder />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile-settings" element={<div className="container mx-auto p-20 font-black text-4xl">Settings Page coming soon...</div>} />
                <Route path="/ticketrise" element={<TicketRise />} />
                <Route path="/support/:id" element={<SupportMessageDetail />} />
                <Route path="/portal/orders" element={<PortalOrders />} />
                <Route path="/portal/track/:id" element={<PortalTrack />} />
                <Route path="/bulk-inquiry" element={<BulkInquiryPage />} />
                <Route path="/request-quote" element={<RequestQuote />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                <Route path="/private-labeling" element={<PrivateLabelingPage />} />
                <Route path="/wholesale" element={<WholesalePage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/company-profile" element={<CompanyProfilePage />} />
                <Route path="/notifications/:id" element={<NotificationDetail />} />
                <Route path="/refund" element={<RefundPage />} />
                <Route path="/refund-request" element={<RefundPage />} />
                <Route path="/cancelled-orders" element={<CancelledOrders />} />
                <Route path="/portal/dashboard" element={<div className="container mx-auto p-20 font-black text-4xl">Dashboard coming soon...</div>} />
                <Route path="/portal/wallet" element={<div className="container mx-auto p-20 font-black text-4xl">Wallet coming soon...</div>} />
                <Route path="/portal/bulk" element={<div className="container mx-auto p-20 font-black text-4xl">Bulk Requests coming soon...</div>} />
                <Route path="*" element={
                  <MainLayout>
                    <div className="min-h-[65vh] flex flex-col items-center justify-center font-outfit text-center px-4">
                      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6 text-4xl font-black text-slate-300">404</div>
                      <h1 className="text-3xl font-black text-slate-900 mb-3 tracking-tight">Page Not Found</h1>
                      <p className="text-slate-400 mb-8 font-medium uppercase tracking-widest text-[10px]">The resource you are looking for has been relocated.</p>
                      <a href="/" className="px-8 py-4 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-[0.98]">Go Back Home</a>
                    </div>
                  </MainLayout>
                } />
              </Routes>
              </Suspense>
            </div>
          </FavoritesProvider>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
